import { GenericAbortSignal } from 'axios';
import { MprcRequestErrorReport } from 'components';
import { MprcRibbonListTabbedHandle, MprcRibbonTabbed, MprcRibbonTabbedItem } from "components/MprcRibbonList";
import { MprcRibbonTabLoading } from 'components/MprcRibbonList/MprcRibbonTabLoading';
import { CarouselCardRenderMode, CarouselConfigDto, CarouselDataResponse, CarouselModule } from 'generated/CoreApi_v1';
import TzUtils from 'lib/TzUtils';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { SessionService } from 'services/sessionService';
import { CardDtoToCardRibbon } from 'views/general/Cards/CardDtoToCardRibbon';
import { HomeCarouselProps } from 'views/home';

////////////////////////////////////////////////////////////////////////////////

export const HomeCarousel = (props: HomeCarouselProps) => {
    const api = useCoreApi();
    const [viewError, setViewError] = useState<any>(null);
    const [loadingConfig, setLoadingConfig] = useState<boolean>(true);
    const [lists, setLists] = useState<MprcRibbonTabbedItem[][]>([]);
    const listRefs = useRef<MprcRibbonListTabbedHandle[]>([]);
    const forceReload = useRef<boolean>(false);
    const forceRefreshRibbons = useRef<Set<number>>(new Set());

    ///
    async function loadCarouselConfig() {
        // Tenta obter a configuração do cache local.
        // Se o cache expirou OU há um novo dado Sensível, obtém uma nova configuração da página.
        try {
            var cfg: CarouselConfigDto | null = null;

            if (!forceReload.current) {

                var cfgSession = SessionService.current.getCarouselHomeConfig();

                if (cfgSession != null) {
                    const response = await api.v1.carousel.checkNewSensitiveData({ module: CarouselModule.Home });
                    if (response.data) cfg = null;
                    else cfg = JSON.parse(cfgSession) as CarouselConfigDto;
                }
            }

            if (cfg == null) {
                const response = await api.v1.carousel.getConfig({ module: CarouselModule.Home });
                cfg = response.data;
                SessionService.current.setCarouselHomeConfig(JSON.stringify(cfg));
            }

            if (cfg != null) {
                if (cfg.items.length == 0)
                    props.onEmptyDataset();
                else setLists(
                    cfg.items.map((rows) => {
                        return rows.map((x) => ({
                            id: x.carouselId,
                            name: x.name,
                            renderMode: x.renderMode
                        } as MprcRibbonTabbedItem));
                    })
                );
            }
        }
        catch (error) {
            setViewError(error);
        }
        finally {
            forceReload.current = false;
            setLoadingConfig(false);
        }
    }


    ///
    const handleGetCarouselData = (ribbonId: number, carouselId: string, carouselIndex: number, renderMode: CarouselCardRenderMode, expanded: boolean, maxItems: number, searchText: string, signal: GenericAbortSignal | undefined) => {
        return new Promise<React.ReactNode[]>(async (resolve, reject) => {

            var data: CarouselDataResponse | null = null;

            try {
                if (data == null) {
                    const needsForceRefresh = forceRefreshRibbons.current.has(ribbonId);
                    if (needsForceRefresh) forceRefreshRibbons.current.delete(ribbonId);

                    const response = await api.v1.carousel.getCarouselData({
                        carouselId: carouselId,
                        searchText: searchText,
                        pageNumber: 1,
                        pageSize: expanded ? 9999 : maxItems + 1,
                        browserTimeZoneId: TzUtils.browserTimezone(),
                        forceRefresh: needsForceRefresh
                    }, { signal });

                    data = response.data;
                }

                resolve(
                    CardDtoToCardRibbon(
                        data.items,
                        renderMode,
                        {
                            onCarouselReload: () => {
                                forceRefreshRibbons.current.add(ribbonId);
                                listRefs.current[ribbonId]?.reset?.();
                            },
                            onFullReload: () => {
                                forceReload.current = true;
                                setLoadingConfig(true);
                            }
                        }
                    )
                );
            }
            catch (e) {
                reject(e);
            }
        });
    };

    ///
    useEffect(() => {
        if (loadingConfig) loadCarouselConfig();
    }, [loadingConfig]);

    ///
    return viewError
        ? <MprcRequestErrorReport error={viewError} />
        : (
            <MprcRibbonTabbed.Container key={"container-lms-carousel"}>
                {
                    (maxWidth, gap, margin) => {

                        return (
                            <div>
                                {
                                    loadingConfig
                                    && <MprcRibbonTabLoading />
                                }
                                {
                                    lists.map((rows, rowIndex) => {
                                        return (
                                            <div key={rowIndex}>
                                                <MprcRibbonTabbed.Tab
                                                    ribbonId={rowIndex}
                                                    items={rows}
                                                    gapWidth={gap}
                                                    cardMargin={margin}
                                                    maxWidth={maxWidth}
                                                    maxItems={20}
                                                    ref={ref => { if (ref != null) listRefs.current.push(ref); }}
                                                    onGetCards={handleGetCarouselData}
                                                />
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        );
                    }
                }
            </MprcRibbonTabbed.Container>
        );
};
